import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { KpiCard } from '@/components/KpiCard';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, BarChart, Bar } from 'recharts';
import { Activity, CheckCircle2, DollarSign, AlertTriangle } from 'lucide-react';

export default function Analytics() {
  const { data } = useQuery({ queryKey: ['overview'], queryFn: () => api.get('/analytics/overview').then(r => r.data) });
  const { data: tasksData } = useQuery({ queryKey: ['tasks', 'all'], queryFn: () => api.get('/tasks', { params: { limit: 200 } }).then(r => r.data) });

  // aggregate actions per hour last 24h from tasks
  const hourly = React.useMemo(() => {
    const items = tasksData?.items || [];
    const buckets = new Map();
    const now = Date.now();
    for (let i = 23; i >= 0; i--) {
      const t = new Date(now - i * 3600 * 1000);
      const k = `${String(t.getHours()).padStart(2, '0')}:00`;
      buckets.set(k, { hour: k, actions: 0, failed: 0 });
    }
    items.forEach((t) => {
      const d = new Date(t.created_at);
      if (now - d.getTime() > 24 * 3600 * 1000) return;
      const k = `${String(d.getHours()).padStart(2, '0')}:00`;
      if (!buckets.has(k)) return;
      const b = buckets.get(k);
      b.actions += 1;
      if (t.status === 'failed') b.failed += 1;
    });
    return Array.from(buckets.values());
  }, [tasksData]);

  const kpis = [
    { label: 'Actions (24h)', value: data?.actions_24h ?? '—', icon: Activity, testId: 'kpi-card-actions-24h' },
    { label: 'Verified real', value: data ? `${Math.round((data.real_rate || 0) * 100)}%` : '—', icon: CheckCircle2, testId: 'kpi-card-verified-real' },
    { label: 'Average risk', value: data ? (data.avg_risk || 0).toFixed(2) : '—', icon: AlertTriangle, testId: 'kpi-card-avg-risk' },
    { label: 'Spend', value: data ? `$${(data.spend_usd || 0).toFixed(2)}` : '—', icon: DollarSign, testId: 'kpi-card-spend' },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <PageHeader eyebrow="Intelligence" title="Analytics" description="Fleet performance, risk trend, and verified outcomes." />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((k) => <KpiCard key={k.label} {...k} />)}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Actions per hour (last 24h)">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={hourly}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(176 78% 44%)" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(176 78% 44%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="hsl(214 30% 20%)" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="hour" stroke="hsl(214 18% 62%)" tick={{ fontSize: 11 }} />
              <YAxis stroke="hsl(214 18% 62%)" tick={{ fontSize: 11 }} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="actions" stroke="hsl(176 78% 44%)" fill="url(#g1)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Failures per hour">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={hourly}>
              <CartesianGrid stroke="hsl(214 30% 20%)" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="hour" stroke="hsl(214 18% 62%)" tick={{ fontSize: 11 }} />
              <YAxis stroke="hsl(214 18% 62%)" tick={{ fontSize: 11 }} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="failed" fill="hsl(0 84% 60%)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
      <div className="border-b border-white/5 px-5 py-3 text-sm font-medium">{title}</div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-white/10 bg-[hsl(var(--surface-2))] px-2.5 py-1.5 text-xs shadow-lg">
      <div className="font-mono text-[hsl(var(--text-3))]">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="mt-0.5 flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="font-mono tabular">{p.value}</span>
        </div>
      ))}
    </div>
  );
}
